import { useQuery } from '@tanstack/react-query'
import { useAccount } from 'wagmi'
import { createPublicClient, http, formatUnits, parseUnits } from 'viem'
import { base } from 'viem/chains'
import { AAVE_V3_BASE_TOKENS } from '../data/tokens'
// Remove this import to avoid duplicate useAaveData calls
import { useTokenBalances } from './useTokenBalances'
import { computeNetApy, formatNetApyForUI } from '../lib/netApy'
import { useUserReserves } from './useUserReserves'

// Aave V3 Pool ABI - for user data and positions
const AAVE_POOL_ABI = [
  {
    "inputs": [{"name": "user", "type": "address"}],
    "name": "getUserAccountData",
    "outputs": [
      {"name": "totalCollateralETH", "type": "uint256"},
      {"name": "totalDebtETH", "type": "uint256"},
      {"name": "availableBorrowsETH", "type": "uint256"},
      {"name": "currentLiquidationThreshold", "type": "uint256"},
      {"name": "ltv", "type": "uint256"},
      {"name": "healthFactor", "type": "uint256"}
    ],
    "stateMutability": "view",
    "type": "function"
  },
  {
    "inputs": [{"name": "user", "type": "address"}],
    "name": "getUserAccountData",
    "outputs": [
      {"name": "totalCollateralETH", "type": "uint256"},
      {"name": "totalDebtETH", "type": "uint256"},
      {"name": "availableBorrowsETH", "type": "uint256"},
      {"name": "currentLiquidationThreshold", "type": "uint256"},
      {"name": "ltv", "type": "uint256"},
      {"name": "healthFactor", "type": "uint256"}
    ],
    "stateMutability": "view",
    "type": "function"
  }
] as const

// Aave V3 Pool address on Base mainnet
const AAVE_POOL_ADDRESS = '0xA238Dd80C259a72e81d7e4664a9801593F98d1c5' // Base mainnet Aave V3 Pool

// Create public client for Base mainnet
const publicClient = createPublicClient({
  chain: base,
  transport: http()
})

// Function to calculate weighted average APY based on token positions
function calculateWeightedAPY(tokenBalances: Record<string, string>, aaveData: any[]): string {
  if (!aaveData || Object.keys(tokenBalances).length === 0) {
    return '0.00%'
  }

  let totalValueUSD = 0
  let weightedAPYSum = 0

  // Calculate weighted average APY
  for (const [tokenAddress, balance] of Object.entries(tokenBalances)) {
    const balanceNum = parseFloat(balance)
    if (balanceNum <= 0) continue

    // Find the token data in Aave reserves
    const tokenData = aaveData.find(reserve => 
      reserve.underlyingAsset.toLowerCase() === tokenAddress.toLowerCase()
    )

    if (tokenData) {
      // For now, we'll use a simple approach - in reality, we'd need to:
      // 1. Get the actual aToken balance (aUSDbC, aUSDC, etc.)
      // 2. Convert to USD value using token prices
      // 3. Calculate weighted average
      
      // Since we don't have individual token positions yet, we'll use the total supplied amount
      // and assume it's all in the token with the highest APY for now
      const apyValue = tokenData.supplyAPY
      const tokenValueUSD = balanceNum // This should be converted to USD using price
      
      totalValueUSD += tokenValueUSD
      weightedAPYSum += apyValue * tokenValueUSD
      
      console.log(`[portfolio] Token ${tokenData.symbol}: ${balance} balance, ${apyValue}% APY`)
    }
  }

  if (totalValueUSD === 0) {
    return '0.00%'
  }

  const weightedAPY = weightedAPYSum / totalValueUSD
  return `${weightedAPY.toFixed(2)}%`
}

// Function to create user positions based on actual Aave-supplied data
async function getUserAavePositions(userAddress: string, totalSuppliedUSD: number, aaveData: any[], tokenBalances: any): Promise<any> {
  try {
    console.log('[portfolio] Creating user positions based on actual Aave-supplied data')
    console.log('[portfolio] Total supplied USD:', totalSuppliedUSD)
    console.log('[portfolio] Total borrowed USD: 0')
    
    // For now, we'll create a simplified position based on the total supplied amount
    // This assumes all supplied funds are in USDbC (most common stablecoin on Base)
    // In the future, we could fetch actual user reserve data from Aave if needed
    
    const userReservesData = []
    
    if (totalSuppliedUSD > 0) {
      // Find USDbC reserve (most common stablecoin on Base)
      const usdbcReserve = aaveData.find(r => 
        r.underlyingAsset?.toLowerCase() === '0xd9aAEc86B65D86f6A7B5B1b0c42FFA531710b6CA'.toLowerCase()
      )
      
      if (usdbcReserve) {
        console.log('[portfolio] Using USDbC reserve for Net APY calculation')
        console.log('[portfolio] USDbC reserve data:', {
          underlyingAsset: usdbcReserve.underlyingAsset,
          liquidityRate: usdbcReserve.liquidityRate,
          priceInUsd: usdbcReserve.priceInUsd || '1.0'
        })
        
        // Create position data for the supplied amount
        userReservesData.push({
          reserve: { 
            underlyingAsset: usdbcReserve.underlyingAsset,
            symbol: 'USDbC',
            decimals: 6
          },
          underlyingBalance: totalSuppliedUSD.toString(), // Direct USD amount for stablecoin
          variableBorrows: '0', // No borrowing
          stableBorrows: '0'
        })
      } else {
        console.log('[portfolio] USDbC reserve not found, using fallback')
        // Fallback: create a generic position
        userReservesData.push({
          reserve: { 
            underlyingAsset: '0xd9aAEc86B65D86f6A7B5B1b0c42FFA531710b6CA',
            symbol: 'USDbC',
            decimals: 6
          },
          underlyingBalance: totalSuppliedUSD.toString(),
          variableBorrows: '0',
          stableBorrows: '0'
        })
      }
    }

    console.log('[portfolio] Created positions for', userReservesData.length, 'Aave-supplied assets')
    console.log('[portfolio] Total supplied USD:', totalSuppliedUSD)

    return {
      userReservesData,
      totalCollateralUSD: totalSuppliedUSD.toString(),
      totalBorrowsUSD: '0'
    }
  } catch (error) {
    console.error('[portfolio] Error creating user Aave positions:', error)
    console.error('[portfolio] Error details:', error.message)
    return {
      userReservesData: [],
      totalCollateralUSD: totalSuppliedUSD.toString(),
      totalBorrowsUSD: '0'
    }
  }
}

// Function to calculate Net APY using our comprehensive utility
async function calculateNetAPY(aaveData: any[], totalSuppliedUSD: number, totalBorrowedUSD: number, userAddress: string): Promise<string> {
  try {
    if (totalSuppliedUSD <= 0) {
      return '0.00%'
    }

    // Get real user Aave positions based on actual Aave-supplied data
    const userPositions = await getUserAavePositions(userAddress, totalSuppliedUSD, aaveData, null)
    
    if (userPositions.userReservesData.length === 0) {
      console.log('[portfolio] No user positions found, falling back to simple calculation')
      // Fallback to simple calculation if no positions found
      const firstReserve = aaveData[0]
      if (!firstReserve) return '0.00%'
      
      const supplyAPY = firstReserve.supplyAPY || 0
      const borrowAPY = firstReserve.borrowAPY || 0
      const netAPY = totalBorrowedUSD > 0 ? supplyAPY - borrowAPY : supplyAPY
      
      return `${netAPY.toFixed(2)}%`
    }

    // Use real user positions for Net APY calculation
    const userSummary = {
      userReservesData: userPositions.userReservesData,
      totalCollateralUSD: totalSuppliedUSD.toString(),
      totalBorrowsUSD: totalBorrowedUSD.toString()
    }

    // Convert Aave data to the format expected by computeNetApy
    const reserves = aaveData.map(reserve => ({
      underlyingAsset: reserve.underlyingAsset,
      priceInUsd: reserve.priceInUsd,
      liquidityRate: reserve.liquidityRate,
      variableBorrowRate: reserve.variableBorrowRate,
      stableBorrowRate: reserve.stableBorrowRate
    }))

    // Debug: Check USDbC reserve data
    const usdbcReserve = reserves.find(reserve => 
      reserve.underlyingAsset?.toLowerCase() === '0xd9aAEc86B65D86f6A7B5B1b0c42FFA531710b6CA'.toLowerCase()
    )
    console.log('[portfolio] USDbC reserve found:', !!usdbcReserve)
    if (usdbcReserve) {
      console.log('[portfolio] USDbC reserve data:', {
        underlyingAsset: usdbcReserve.underlyingAsset,
        liquidityRate: usdbcReserve.liquidityRate,
        priceInUsd: usdbcReserve.priceInUsd,
        supplyAPY: usdbcReserve.supplyAPY
      })
      console.log('[portfolio] USDbC reserve ALL fields:', usdbcReserve)
      
      // Calculate supplyAPY if it's missing
      if (usdbcReserve.supplyAPY === undefined && usdbcReserve.liquidityRate) {
        const calculatedSupplyAPY = (parseFloat(usdbcReserve.liquidityRate) / 1e27) * 100
        console.log('[portfolio] Calculating missing supplyAPY:', calculatedSupplyAPY)
        usdbcReserve.supplyAPY = calculatedSupplyAPY
      }
      
      // Fix: Add missing priceInUsd for USDbC (stablecoin = $1.00)
      if (!usdbcReserve.priceInUsd) {
        console.log('[portfolio] Adding missing priceInUsd for USDbC: 1.0')
        usdbcReserve.priceInUsd = '1.0'
      }
    }

    // Calculate Net APY with real positions (only if aaveData is available)
    let netApy = '0.00%'
    if (aaveData && aaveData.length > 0) {
      const netApyResult = computeNetApy({ userSummary, reserves })
      
      console.log('[portfolio] Net APY calculation result with real positions:', netApyResult)
      console.log('[portfolio] Detailed Net APY components:', {
        netApyOnEquity: netApyResult.netApyOnEquity,
        netApyOnSupplied: netApyResult.netApyOnSupplied,
        supplyYieldUSD: netApyResult.components.supplyYieldUSD,
        borrowCostUSD: netApyResult.components.borrowCostUSD,
        totalCollateralUSD: netApyResult.components.totalCollateralUSD,
        totalDebtUSD: netApyResult.components.totalDebtUSD,
        equityUSD: netApyResult.components.equityUSD,
        note: netApyResult.note
      })
      
      // Return the equity-based Net APY formatted as percentage
      netApy = formatNetApyForUI(netApyResult.netApyOnEquity)
    } else {
      console.log('[portfolio] No aaveData available, using default Net APY: 0.00%')
    }
    
    return netApy
  } catch (error) {
    console.error('[portfolio] Error calculating Net APY:', error)
    return '0.00%'
  }
}

interface PortfolioData {
  totalSupplied: string
  totalBorrowed: string
  availableBorrows: number
  healthFactor: number
  netAPY: string
  yieldEstimate: string
  utilization: number
  ltv: number
  positions: number
  isLoading: boolean
  error: string | null
}

async function fetchPortfolioData(address: `0x${string}`, aaveData?: any[], userReserves?: any[]): Promise<PortfolioData> {
  try {
    console.log('[portfolio] Fetching data for address:', address)
    
    // Get user account data from Aave V3 Pool
    const userData = await publicClient.readContract({
      address: AAVE_POOL_ADDRESS,
      abi: AAVE_POOL_ABI,
      functionName: 'getUserAccountData',
      args: [address]
    })

    console.log('[portfolio] User account data:', userData)

    const [
      totalCollateralETH,
      totalDebtETH,
      availableBorrowsETH,
      currentLiquidationThreshold,
      ltv,
      healthFactor
    ] = userData

    // Aave V3 returns values in 8 decimals (USD format), not 18 decimals (ETH format)
    // The raw value 101973380 with 8 decimals = 1.0197338 USD
    const totalSuppliedUSD = parseFloat(formatUnits(totalCollateralETH, 8))
    const totalBorrowedUSD = parseFloat(formatUnits(totalDebtETH, 8))
    const availableBorrowsUSD = parseFloat(formatUnits(availableBorrowsETH, 8))
    const healthFactorNum = parseFloat(formatUnits(healthFactor, 18))
    const ltvNum = parseFloat(formatUnits(ltv, 4)) // LTV is in basis points (10000 = 100%)
    
    console.log('[portfolio] 🔍 LTV calculation:')
    console.log('[portfolio] ltv (raw):', ltv.toString())
    console.log('[portfolio] ltvNum (after formatUnits):', ltvNum)
    console.log('[portfolio] ltvNum (rounded):', Math.round(ltvNum))
    
    console.log('[portfolio] 🔍 Corrected conversion (8 decimals for USD values):')
    console.log('[portfolio] totalSuppliedUSD:', totalSuppliedUSD)
    console.log('[portfolio] totalBorrowedUSD:', totalBorrowedUSD)
    console.log('[portfolio] availableBorrowsUSD:', availableBorrowsUSD)
    console.log('[portfolio] healthFactorNum:', healthFactorNum)
    
    console.log('[portfolio] 🔍 Raw values from Aave:')
    console.log('[portfolio] totalCollateralETH (raw):', totalCollateralETH.toString())
    console.log('[portfolio] totalDebtETH (raw):', totalDebtETH.toString())
    console.log('[portfolio] healthFactor (raw):', healthFactor.toString())
    
    console.log('[portfolio] 🔍 After formatUnits conversion:')
    console.log('[portfolio] totalSuppliedUSD:', totalSuppliedUSD)
    console.log('[portfolio] totalBorrowedUSD:', totalBorrowedUSD)
    console.log('[portfolio] healthFactorNum:', healthFactorNum)
    console.log('[portfolio] ltvNum:', ltvNum)
    
    // Check if user has any meaningful Aave positions using correct USD values
    const hasAavePositions = totalSuppliedUSD > 0.01 || totalBorrowedUSD > 0.01
    
    console.log('[portfolio] 🔍 Position analysis (corrected):')
    console.log('[portfolio] hasAavePositions:', hasAavePositions)
    console.log('[portfolio] totalSuppliedUSD > 0.01:', totalSuppliedUSD > 0.01)
    console.log('[portfolio] totalBorrowedUSD > 0.01:', totalBorrowedUSD > 0.01)
    
    if (!hasAavePositions) {
      console.log('[portfolio] ❌ No meaningful Aave positions found')
      return {
        totalSupplied: '0.00',
        totalBorrowed: '0.00',
        availableBorrows: 0,
        healthFactor: 0,
        netAPY: '0.00%',
        yieldEstimate: '0.00',
        utilization: 0,
        ltv: 0,
        positions: 0,
        isLoading: false,
        error: null
      }
    }
    
    // Handle health factor for supply-only positions
    const MAX_SAFE_HEALTH_FACTOR = 1e10
    const isInfiniteHealthFactor = healthFactorNum > MAX_SAFE_HEALTH_FACTOR
    const effectiveHealthFactor = isInfiniteHealthFactor ? 999.99 : healthFactorNum
    
    console.log('[portfolio] 🔍 Health factor analysis:')
    console.log('[portfolio] healthFactorNum:', healthFactorNum)
    console.log('[portfolio] isInfiniteHealthFactor:', isInfiniteHealthFactor)
    console.log('[portfolio] effectiveHealthFactor:', effectiveHealthFactor)
    console.log('[portfolio] totalSuppliedUSD:', totalSuppliedUSD)
    console.log('[portfolio] totalBorrowedUSD:', totalBorrowedUSD)
    
    // Calculate utilization
    const utilization = (totalSuppliedUSD > 0 && totalBorrowedUSD > 0) ? (totalBorrowedUSD / totalSuppliedUSD) * 100 : 0
    
    // Calculate Net APY using our comprehensive utility
    let netAPY = '0.00%'
    
    console.log('[portfolio] 🔍 Net APY calculation check:')
    console.log('[portfolio] totalSuppliedUSD > 0:', totalSuppliedUSD > 0)
    console.log('[portfolio] aaveData exists:', !!aaveData)
    console.log('[portfolio] aaveData length:', aaveData?.length || 0)
    console.log('[portfolio] aaveData:', aaveData)
    
    if (totalSuppliedUSD > 0 && aaveData && aaveData.length > 0) {
      console.log('[portfolio] Starting Net APY calculation...')
      try {
        // Use our comprehensive Net APY calculation with real user positions
        netAPY = await calculateNetAPY(aaveData, totalSuppliedUSD, totalBorrowedUSD, address)
        console.log('[portfolio] Calculated Net APY:', netAPY)
      } catch (error) {
        console.error('[portfolio] Error in Net APY calculation:', error)
        netAPY = '0.00%'
      }
    } else if (totalSuppliedUSD > 0) {
      // Fallback: Use a default APY when we have supply but no aaveData yet
      console.log('[portfolio] Using default APY while aaveData loads')
      netAPY = '3.80%' // Default APY for USDbC
    } else {
      // No supply or aaveData available
      console.log('[portfolio] No supply or aaveData available')
      netAPY = '0.00%'
    }
    
    // Calculate estimated monthly yield using real APY
    const apyValue = parseFloat(netAPY.replace(/[+%]/g, '')) / 100
    const monthlyYield = totalSuppliedUSD > 0 ? (totalSuppliedUSD * apyValue / 12) : 0
    
    console.log('[portfolio] 🔍 Monthly yield calculation:')
    console.log('[portfolio] netAPY:', netAPY)
    console.log('[portfolio] apyValue:', apyValue)
    console.log('[portfolio] totalSuppliedUSD:', totalSuppliedUSD)
    console.log('[portfolio] monthlyYield (raw):', monthlyYield)
    
    // Count positions - use a simple approach that doesn't require user reserves
    let positions = 0
    
    if (totalSuppliedUSD > 0 || totalBorrowedUSD > 0) {
      // For now, we'll estimate positions based on the total supplied amount
      // This is a rough approximation - in reality we'd need individual token data
      // But it's better than showing 0 or causing rate limiting issues
      positions = 1 // This should be calculated from actual user reserve data when available
      console.log('[portfolio] 🔍 Position count (estimated):', positions)
    } else {
      positions = 0
    }
    
    return {
      totalSupplied: totalSuppliedUSD.toFixed(2),
      totalBorrowed: totalBorrowedUSD.toFixed(2),
      availableBorrows: availableBorrowsUSD,
      healthFactor: effectiveHealthFactor,
      netAPY,
      yieldEstimate: monthlyYield < 0.01 ? monthlyYield.toFixed(4) : monthlyYield.toFixed(2),
      utilization: Math.round(utilization),
      ltv: Math.round(ltvNum * 100), // Convert to percentage (0.75 -> 75)
      positions,
      isLoading: false,
      error: null
    }
    
  } catch (error) {
    console.error('[portfolio] Error fetching portfolio data:', error)
    
    // Check if it's a contract error (contract doesn't exist or user has no positions)
    const isContractError = error instanceof Error && (
      error.message.includes('returned no data') ||
      error.message.includes('ContractFunctionZeroDataError') ||
      error.message.includes('not a contract')
    )
    
    if (isContractError) {
      console.log('[portfolio] User has no Aave positions or contract not available on Base')
    }
    
    // Return zero values if user has no Aave positions or contract not available
    return {
      totalSupplied: '0.00',
      totalBorrowed: '0.00',
      availableBorrows: 0,
      healthFactor: 0,
      netAPY: '0.00%',
      yieldEstimate: '0.00',
      utilization: 0,
      ltv: 0,
      positions: 0,
      isLoading: false,
      error: null
    }
  }
}

export function usePortfolioData(aaveData?: any) {
  const { address, isConnected } = useAccount()
  const { data: userReserves } = useUserReserves(aaveData, false) // Disable user reserves to avoid rate limiting

  return useQuery({
    queryKey: ['portfolio-data', address, aaveData, userReserves],
    queryFn: () => fetchPortfolioData(address!, aaveData, userReserves),
    enabled: !!address && isConnected,
    staleTime: 300000, // 5 minutes - much longer cache time
    refetchInterval: 600000, // 10 minutes - much less frequent to avoid rate limits
    retry: 1, // Only retry once on failure
    refetchOnWindowFocus: false, // Disable to avoid unnecessary refetches
    refetchOnMount: false, // Don't refetch on component mount if data exists
    refetchOnReconnect: false, // Don't refetch on network reconnect
  })
}
